import { useRouterState } from '@tanstack/react-router';
import { useTranslator } from './useTranslator';

function toLabel(segment: string) {
  return segment.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function isId(segment: string) {
  // UUID
  if (
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      segment,
    )
  ) {
    return true;
  }
  // Pure numeric id
  if (/^\d+$/.test(segment)) return true;
  // cuid / nanoid / prefixed ULIDs (e.g. `style_01KKXW391NZT89S2TS4V0E5AKH`)
  // and similar opaque IDs — long alphanumeric strings (underscore allowed)
  // that contain at least one digit. The digit requirement prevents normal
  // page names (`variants`, `products`, `settings`, `my_styles`, etc.) from
  // being mistaken for IDs and stripped from the breadcrumb trail.
  return /^[0-9a-zA-Z_]{8,}$/.test(segment) && /\d/.test(segment);
}

/**
 * Translation key for the trailing crumb of a detail route (`…/$id`).
 * Keyed by the section segment that precedes the id. Defaults to
 * `pages.view`; add an entry here only when a section needs a different
 * verb (e.g. `network: 'pages.profile'`).
 */
const DETAIL_VERB_KEY: Record<string, string> = {};

export function useBreadcrumbTrail() {
  // Use resolvedLocation so breadcrumb updates only after loader completes.
  const pathname =
    useRouterState({
      select: s => s.resolvedLocation?.pathname ?? s.location.pathname,
    }) ?? '/';
  const { getTranslation: t } = useTranslator({ page: 'appHeader' });

  function translateSegment(segment: string): string {
    const key = `pages.${segment}`;
    const translated = t(key);
    // t() returns the key itself when missing — fall back to toLabel
    return translated !== key ? translated : toLabel(segment);
  }

  if (pathname === '/') return [{ label: t('pages.dashboard') }];

  const rawSegments = pathname.split('/').filter(Boolean);
  const segments = rawSegments.filter(s => !isId(s));

  if (segments.length === 0) {
    const last = rawSegments[rawSegments.length - 1] ?? '';
    return [{ label: translateSegment(last) }];
  }

  const hrefAt = (i: number) => `/${segments.slice(0, i + 1).join('/')}`;

  // Detail route — path ends with an id. Show the action verb (default
  // "View") instead of fetching the entity name.
  const lastRaw = rawSegments[rawSegments.length - 1] ?? '';
  if (isId(lastRaw) && !segments.includes(lastRaw)) {
    const section = segments[segments.length - 1] ?? '';
    const verbKey = DETAIL_VERB_KEY[section] ?? 'pages.view';
    const parentSegments = segments.map((seg, i) => ({
      label: translateSegment(seg),
      href: hrefAt(i),
    }));
    return [...parentSegments, { label: t(verbKey) }];
  }

  if (segments.length === 1)
    return [{ label: translateSegment(segments[0] ?? '') }];

  return segments.map((seg, i) =>
    i < segments.length - 1
      ? { label: translateSegment(seg), href: hrefAt(i) }
      : { label: translateSegment(seg) },
  );
}
